// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import Tooltip, { TooltipPresentationProps } from "@werner/MobiusOverrides/Tooltip/Tooltip";
import { color, ellipsis, mergeWithCss } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
}

type Props = OwnProps;

// XNG-Changes: add tooltip style
export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
    tooltip?: TooltipPresentationProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        color: "common.ui3",
        mergeCss: true,
        css: css`
            width: auto;
            min-height: 44px;
            align-items: flex-start;
            align-self: baseline;
            &:hover > ${TypographyStyle} {
                color: ${color("primary")};
            }
        `,
        typographyProps: {
            size: 18,
            weight: 400,
            css: css`
                width: 100%;
                line-height: 22px;
                ${ellipsis(2)}
                ${wrapInContainerStyles}
            `,
        },
    },
    productDetailDescription: {
        size: 18,
        weight: 400,
        color: "common.ui3",
        css: css`
            width: 100%;
            min-height: 44px;
            line-height: 22px;
            ${ellipsis(2)}
            ${wrapInContainerStyles}
        `,
    },
};

/**
 * Xng-Changes
 */
const ProductDescription = ({ product, onClick, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeWithCss(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Tooltip
            {...styles.tooltip}
            text={description}
            triggerComponent={
                <Link
                    {...styles.productDetailLink}
                    href={productDetailPath}
                    alt={`See product details for ${description}`}
                    onClick={onClick}
                    data-test-selector="productDescriptionLink"
                    aria-label={`See product details for ${description}`}
                >
                    {description}
                </Link>
            }
        />
    ) : (
        <Tooltip
            text={description}
            triggerComponent={<Typography {...styles.productDetailDescription}>{description}</Typography>}
        />
    );
};

export default ProductDescription;
